// Cave84 — PWA install prompt hook (Phase 7)
// Captures the browser's `beforeinstallprompt` event so we can surface our
// own install button in TopBar instead of the default browser chip.
// Only fires on supported browsers (Chrome, Edge on Android/Desktop).

import { useEffect, useState } from 'react';

interface BeforeInstallPromptEvent extends Event {
  readonly platforms: string[];
  readonly userChoice: Promise<{ outcome: 'accepted' | 'dismissed'; platform: string }>;
  prompt(): Promise<void>;
}

interface UseInstallPromptResult {
  /** true if the app can be installed (prompt captured, not yet installed) */
  canInstall: boolean;
  /** Trigger the native install dialog */
  promptInstall: () => Promise<void>;
  /** true while the install prompt is pending user response */
  isInstalling: boolean;
}

export function useInstallPrompt(): UseInstallPromptResult {
  const [deferredPrompt, setDeferredPrompt] = useState<BeforeInstallPromptEvent | null>(null);
  const [isInstalling, setIsInstalling] = useState(false);

  useEffect(() => {
    function onBeforeInstall(e: Event) {
      // Prevent the default mini-infobar from appearing.
      e.preventDefault();
      setDeferredPrompt(e as BeforeInstallPromptEvent);
    }

    function onAppInstalled() {
      // App was installed — clear the prompt so the button disappears.
      setDeferredPrompt(null);
    }

    window.addEventListener('beforeinstallprompt', onBeforeInstall);
    window.addEventListener('appinstalled', onAppInstalled);

    return () => {
      window.removeEventListener('beforeinstallprompt', onBeforeInstall);
      window.removeEventListener('appinstalled', onAppInstalled);
    };
  }, []);

  async function promptInstall() {
    if (!deferredPrompt) return;
    setIsInstalling(true);
    try {
      await deferredPrompt.prompt();
      const { outcome } = await deferredPrompt.userChoice;
      if (outcome === 'accepted') {
        setDeferredPrompt(null);
      }
    } finally {
      setIsInstalling(false);
    }
  }

  return {
    canInstall: deferredPrompt !== null,
    promptInstall,
    isInstalling,
  };
}
